import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useMapContext } from "@evergis/react";
import { toWgs84 } from "@turf/turf";

import { getProjectLayers } from "../../ducks/project/selectors";
import { getAppConfig } from "../../ducks/appConfig";
import { metersPerPixel } from "../../utils/metersPerPixel";
import { useGeometrySelection } from "./useGeometrySelection";
import { useGeometrySelectionBuffer } from "./useGeometrySelectionBuffer";
import { useLayersToSelect } from "../../modules/FeatureSelector/hooks/useLayersToSelect";

import { SelectionHistoryFeature } from "../../ducks/geometrySelection/geometrySelection";
import { GeometrySelectionTypes } from "../../modules/Map/components/GeometrySelectionTools/types";

const getLines = (input: SelectionHistoryFeature[]) => {
  return input
    .map(feature => `(${feature._rings[0].map(ringItem => `${ringItem[0]} ${ringItem[1]}`).join(", ")})`)
    .join(", ");
};

const getPolygons = (input: SelectionHistoryFeature[], closePolygons?: boolean) => {
  return `${input
    .map(
      feature =>
        `(${feature._rings
          .map(
            ring =>
              `(${[
                ...ring.map(ringItem => `${ringItem[0]} ${ringItem[1]}`),
                closePolygons && ring[0] ? `${ring[0][0]} ${ring[0][1]}` : null,
              ]
                .filter(Boolean)
                .join(", ")})`,
          )
          .join(", ")})`,
    )
    .join(", ")}`;
};

export const geometryToWkt = (
  features: SelectionHistoryFeature[] = [],
  type?: GeometrySelectionTypes,
  closePolygons?: boolean,
): string => {
  let result = "";

  switch (type) {
    case GeometrySelectionTypes.Point:
      result = `${features.length > 1 ? "MULTIPOINT" : "POINT"}(${features
        .map(feature => `${feature._position[0]} ${feature._position[1]}`)
        .join(", ")})`;
      break;
    case GeometrySelectionTypes.Line:
    case GeometrySelectionTypes.Route:
      result = `${features.length > 1 ? `MULTILINESTRING(${getLines(features)})` : `LINESTRING${getLines(features)}`}`;
      break;
    case GeometrySelectionTypes.Circle:
    case GeometrySelectionTypes.Rect:
    case GeometrySelectionTypes.Polygon:
      result = `${
        features.length > 1
          ? `MULTIPOLYGON(${getPolygons(features, closePolygons)})`
          : `POLYGON${getPolygons(features, closePolygons)}`
      }`;
      break;
    default:
      break;
  }

  return result;
};

export const useGeometrySelectionConditions = (): Record<string, string> => {
  const { map } = useMapContext();
  const layersToSelect = useLayersToSelect();
  const currentProjectLayers = useSelector(getProjectLayers);
  const [{ active, type, history, settings }] = useGeometrySelection();
  const includeSign = useMemo(() => (settings?.selectOutside ? "!" : ""), [settings?.selectOutside]);
  const buffer = useGeometrySelectionBuffer();
  const appConfig = useSelector(getAppConfig);

  const wmsGeometry = useMemo(
    () => `"${geometryToWkt(history.features[history.step], type)}"`,
    [history.features, history.step, type],
  );

  const bufferedGeometry = useMemo(() => {
    if (buffer === undefined && type === GeometrySelectionTypes.Point) {
      const pointPosition = history.features[history.step]?.[0]?._position;

      const bufferSize =
        Array.isArray(pointPosition) && typeof pointPosition[0] === "number" && typeof pointPosition[1] === "number"
          ? Math.abs(
              (appConfig?.settings?.selectBufferFactor ?? 5) *
                metersPerPixel(map.getScaleLevel(), toWgs84(pointPosition)[1]),
            )
          : 100;

      return `buffer(${wmsGeometry}, ${bufferSize < 10 ? "10.00" : bufferSize}, ${map?.crs?.wkid})`;
    }

    if (buffer === undefined && [GeometrySelectionTypes.Line, GeometrySelectionTypes.Route].includes(type)) {
      return `buffer(${wmsGeometry}, 100.00, ${map?.crs?.wkid})`;
    }

    if (buffer !== undefined && settings?.bufferInclude) {
      return `buffer(${wmsGeometry}, ${buffer}, ${map?.crs?.wkid})`;
    }

    return wmsGeometry;
  }, [
    buffer,
    type,
    settings?.bufferInclude,
    wmsGeometry,
    history.features,
    history.step,
    appConfig?.settings?.selectBufferFactor,
    map,
  ]);

  return useMemo(() => {
    if (active && history.step > -1 && history.features[history.step].length > 0) {
      return layersToSelect.layerNames.reduce((acc, curr) => {
        return {
          ...acc,
          [curr]:
            ["QueryLayerService", "PostgresLayerService", "ProxyService"].includes(currentProjectLayers[curr]?.type) &&
            currentProjectLayers[curr]?.geometryAttribute
              ? `${includeSign}intersect(${currentProjectLayers[curr]?.geometryAttribute}, ${bufferedGeometry}, ${map?.crs?.wkid})`
              : undefined,
        };
      }, {});
    }

    return undefined;
  }, [
    active,
    history.step,
    history.features,
    layersToSelect,
    includeSign,
    currentProjectLayers,
    bufferedGeometry,
    map?.crs?.wkid,
  ]);
};
